import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@core/button";
import { Text } from "@core/text";
import {
  ClipboardCheck,
  Eye,
  Sparkles,
  Plus,
  X,
  Lightbulb,
  AlertTriangle,
} from "lucide-react";
import { GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType } from "@packages/api/madlenAPI.schemas";

// Types
interface UbdAssessmentExperiencesStepProps {
  planId?: number;
  ubdPerformanceTasks: string[];
  ubdOtherEvidence: string[];
  isLoading: boolean;
  onGenerateWithAI: (
    generationType: GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType,
  ) => void;
  onFieldChange: (field: string, value: string[]) => void;
}

const UbdAssessmentExperiencesStep: React.FC<
  UbdAssessmentExperiencesStepProps
> = ({
  planId,
  ubdPerformanceTasks,
  ubdOtherEvidence,
  isLoading,
  onGenerateWithAI,
  onFieldChange,
}) => {
  // Initialize arrays with proper split - simplified approach
  const initializeArray = useCallback((contentArray: string[]): string[] => {
    if (!contentArray || contentArray.length === 0) {
      return ["", "", ""];
    }
    return contentArray.map((content) => content.trim());
  }, []);

  // Initialize arrays - simplified state management
  const [performanceTasks, setPerformanceTasks] = useState<string[]>(() =>
    initializeArray(ubdPerformanceTasks),
  );
  const [otherEvidence, setOtherEvidence] = useState<string[]>(() =>
    initializeArray(ubdOtherEvidence),
  );

  // Popover state for warning
  const [showWarningPopover, setShowWarningPopover] = useState(false);

  // Check if there's existing data
  const hasExistingData = useCallback(() => {
    const hasPerformanceTasks = performanceTasks.some(
      (task) => task.trim() !== "",
    );
    const hasOtherEvidence = otherEvidence.some(
      (evidence) => evidence.trim() !== "",
    );

    return hasPerformanceTasks || hasOtherEvidence;
  }, [performanceTasks, otherEvidence]);

  useEffect(() => {
    if (ubdPerformanceTasks.length > 0 || ubdOtherEvidence.length > 0) {
      if (performanceTasks.toString() !== ubdPerformanceTasks.toString()) {
        setPerformanceTasks(initializeArray(ubdPerformanceTasks));
      }
      if (otherEvidence.toString() !== ubdOtherEvidence.toString()) {
        setOtherEvidence(initializeArray(ubdOtherEvidence));
      }
    }
  }, [
    ubdPerformanceTasks,
    ubdOtherEvidence,
    initializeArray,
    performanceTasks,
    otherEvidence,
  ]);

  // Track loading states
  const [loadingStates, setLoadingStates] = useState({
    performanceTasks: false,
    otherEvidence: false,
  });

  // Update loading states when parent loading state changes
  useEffect(() => {
    if (!isLoading) {
      setLoadingStates({
        performanceTasks: false,
        otherEvidence: false,
      });
    }
  }, [isLoading]);

  // Array update functions - simplified
  const updatePerformanceTask = useCallback(
    (index: number, content: string) => {
      setPerformanceTasks((prev) => {
        const updated = [...prev];
        updated[index] = content;
        onFieldChange("ubdPerformanceTasks", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  const updateOtherEvidence = useCallback(
    (index: number, content: string) => {
      setOtherEvidence((prev) => {
        const updated = [...prev];
        updated[index] = content;
        onFieldChange("ubdOtherEvidence", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  // Add functions - simplified
  const addPerformanceTask = useCallback(() => {
    setPerformanceTasks((prev) => {
      const updated = [...prev, ""];
      onFieldChange("ubdPerformanceTasks", updated);
      return updated;
    });
  }, [onFieldChange]);

  const addOtherEvidence = useCallback(() => {
    setOtherEvidence((prev) => {
      const updated = [...prev, ""];
      onFieldChange("ubdOtherEvidence", updated);
      return updated;
    });
  }, [onFieldChange]);

  // Remove functions - simplified
  const removePerformanceTask = useCallback(
    (index: number) => {
      setPerformanceTasks((prev) => {
        if (prev.length <= 1) return prev;
        const updated = prev.filter((_, i) => i !== index);
        onFieldChange("ubdPerformanceTasks", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  const removeOtherEvidence = useCallback(
    (index: number) => {
      setOtherEvidence((prev) => {
        if (prev.length <= 1) return prev;
        const updated = prev.filter((_, i) => i !== index);
        onFieldChange("ubdOtherEvidence", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  // AI GENERATION FUNCTIONS - updated to use modal system
  const generatePerformanceTasksAI = useCallback(() => {
    if (!planId) {
      console.error("Plan ID is required for AI generation");
      return;
    }

    onGenerateWithAI("PERFORMANCE_TASKS");
  }, [planId, onGenerateWithAI]);

  const generateOtherEvidenceAI = useCallback(() => {
    if (!planId) {
      console.error("Plan ID is required for AI generation");
      return;
    }

    onGenerateWithAI("OTHER_EVIDENCE");
  }, [planId, onGenerateWithAI]);

  // Generate all sections with AI
  const handleGenerateAll = useCallback(() => {
    if (!planId) {
      console.error("Plan ID is required for AI generation");
      return;
    }

    if (hasExistingData()) {
      setShowWarningPopover(true);
    } else {
      setLoadingStates({
        performanceTasks: true,
        otherEvidence: true,
      });

      onGenerateWithAI("ASSESSMENT_AND_EXPERIENCES");
    }
  }, [planId, onGenerateWithAI, hasExistingData]);

  // Confirm generate all
  const confirmGenerateAll = () => {
    setShowWarningPopover(false);
    setLoadingStates({
      performanceTasks: true,
      otherEvidence: true,
    });
    onGenerateWithAI("ASSESSMENT_AND_EXPERIENCES");
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showWarningPopover && event.target instanceof Element) {
        const popover = document.querySelector(".warning-popover");
        const button = document.querySelector(".generate-all-button");

        if (
          popover &&
          !popover.contains(event.target) &&
          button &&
          !button.contains(event.target)
        ) {
          setShowWarningPopover(false);
        }
      }
    };

    if (showWarningPopover) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showWarningPopover]);

  // Check if any section is loading
  const isAnyLoading =
    loadingStates.performanceTasks || loadingStates.otherEvidence || isLoading;

  // Auto resize function for textareas
  const autoResize = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 40), 80);
    textarea.style.height = newHeight + "px";
  };

  // Auto-resize all textareas when data changes
  useEffect(() => {
    const resizeAllTextareas = () => {
      const textareas = document.querySelectorAll("textarea");
      textareas.forEach((textarea) => {
        if (textarea.value) {
          autoResize(textarea as HTMLTextAreaElement);
        }
      });
    };

    // Small delay to ensure DOM is updated
    const timer = setTimeout(resizeAllTextareas, 0);

    return () => clearTimeout(timer);
  }, [performanceTasks, otherEvidence]);

  // Render Section Cards Function - simplified to use array indices
  const renderSectionCards = (
    items: string[],
    updateItem: (index: number, content: string) => void,
    addItem: () => void,
    removeItem: (index: number) => void,
    generateAI: () => void,
    config: {
      title: string;
      icon: React.ReactNode;
      placeholder: string;
      description: string;
      isLoading: boolean;
      colorClasses: {
        iconColor: string;
        cardBg: string;
        cardBorder: string;
        focusRing: string;
        aiButton: string;
        addButtonHover: string;
      };
    },
  ) => (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div
            className={`p-2 rounded-lg ${config.colorClasses.iconColor} shadow-sm`}
          >
            {config.icon}
          </div>
          <div>
            <Text
              variant="h3"
              className="text-lg font-bold text-gray-900 dark:text-gray-100"
            >
              {config.title}
            </Text>
            <Text
              variant="p"
              size="sm"
              className="text-gray-600 dark:text-gray-400 max-w-xs"
            >
              {config.description}
            </Text>
          </div>
        </div>

        {/* AI Generate Button */}
        <Button
          variant="solid"
          size="sm"
          onClick={generateAI}
          disabled={config.isLoading || !planId}
          className={`px-3 py-2 text-white text-xs rounded-lg transition-all duration-300 disabled:opacity-50 hover:scale-105 transform hover:shadow-lg font-medium ${config.colorClasses.aiButton}`}
        >
          {config.isLoading ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent mr-2"></div>
              <span className="animate-pulse">...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-3 h-3 mr-2" />
              AI
            </>
          )}
        </Button>
      </div>

      {/* Cards */}
      <div className="space-y-3">
        {items.map((item, index) => (
          <div
            key={index}
            className={`relative group ${config.colorClasses.cardBg} ${config.colorClasses.cardBorder} rounded-xl pl-2 shadow-sm hover:shadow-md transition-all duration-300`}
          >
            {/* Card Number */}
            <div className="absolute -top-2 -left-2 w-6 h-6 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-400 shadow-sm">
              {index + 1}
            </div>

            {/* Remove Button */}
            {items.length > 1 && (
              <button
                onClick={() => removeItem(index)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-sm hover:scale-110"
              >
                <X className="w-3 h-3" />
              </button>
            )}

            {/* Content Textarea */}
            <textarea
              value={item}
              onChange={(e) => {
                updateItem(index, e.target.value);
                autoResize(e.target);
              }}
              onInput={(e) => autoResize(e.target as HTMLTextAreaElement)}
              placeholder={config.placeholder}
              disabled={isAnyLoading}
              className={`w-full bg-transparent border-0 focus:outline-none focus:ring-0 resize-none text-sm text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 ${config.colorClasses.focusRing} rounded-lg p-2 min-h-[40px] ${isAnyLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              style={{ height: "auto" }}
              rows={1}
            />
          </div>
        ))}

        {/* Add New Card Button */}
        <button
          type="button"
          onClick={addItem}
          disabled={isAnyLoading}
          className={`w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl transition-all duration-300 ${config.colorClasses.addButtonHover} group disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]`}
        >
          <div className="flex items-center justify-center space-x-2 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
            <Plus className="w-4 h-4" />
            <Text variant="span" size="sm" className="font-medium">
              Yeni Ekle
            </Text>
          </div>
        </button>
      </div>
    </div>
  );

  // Cleanup function for timeouts - simplified (no timeouts to clear)
  useEffect(() => {
    return () => {
      // No cleanup needed for simplified state management
    };
  }, []);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Professional Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-xl p-4 border border-orange-200 dark:border-orange-700 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl shadow-md hover:scale-110 transition-transform duration-200">
            <Eye className="w-5 h-5 text-white" />
          </div>
          <div>
            <Text
              variant="h2"
              className="text-lg font-bold text-gray-900 dark:text-gray-100"
            >
              UBD Değerlendirme & Öğrenme Deneyimleri
            </Text>
            <Text
              variant="p"
              size="sm"
              className="text-gray-600 dark:text-gray-400"
            >
              AI ile değerlendirme yöntemleri ve öğrenme etkinlikleri oluşturun
            </Text>
          </div>
        </div>

        <div className="relative">
          <Button
            variant="solid"
            onClick={handleGenerateAll}
            disabled={isAnyLoading || !planId}
            className="generate-all-button px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white text-sm rounded-xl transition-all duration-300 disabled:opacity-50 hover:scale-105 transform hover:shadow-xl font-semibold"
          >
            {isAnyLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-3"></div>
                <span className="animate-pulse">Tümü Oluşturuluyor...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-3 animate-pulse" />
                Tümünü AI ile Oluştur
              </>
            )}
          </Button>

          {/* Warning Popover */}
          {showWarningPopover && (
            <div className="warning-popover absolute top-full right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 z-50 animate-fade-in">
              {/* Triangle pointer */}
              <div className="absolute -top-2 right-6 w-4 h-4 bg-white dark:bg-gray-800 border-l border-t border-gray-200 dark:border-gray-700 transform rotate-45"></div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                </div>
                <div className="flex-1">
                  <Text
                    variant="p"
                    className="text-gray-800 dark:text-gray-200 font-medium text-sm mb-2"
                  >
                    Mevcut İçeriği Değiştir
                  </Text>
                  <Text
                    variant="p"
                    className="text-gray-600 dark:text-gray-400 text-xs mb-1"
                  >
                    Değerlendirme ve öğrenme deneyimleri bölümlerinde mevcut
                    içerik bulunuyor.
                  </Text>
                  <Text
                    variant="p"
                    className="text-gray-600 dark:text-gray-400 text-xs mb-3"
                  >
                    AI ile yeni içerik oluşturduğunuzda,{" "}
                    <strong>tüm mevcut içerikleriniz silinecek</strong> ve
                    yerine AI tarafından oluşturulan yeni içerik gelecektir.
                  </Text>
                  <div className="flex space-x-2">
                    <Button
                      variant="solid"
                      size="sm"
                      onClick={() => setShowWarningPopover(false)}
                      className="px-3 py-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-xs rounded-md"
                    >
                      İptal
                    </Button>
                    <Button
                      variant="solid"
                      size="sm"
                      onClick={confirmGenerateAll}
                      className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded-md"
                    >
                      Evet, Değiştir
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {planId ? (
        <>
          {/* 2x2 Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Performance Tasks - Purple Character */}
            {renderSectionCards(
              performanceTasks,
              updatePerformanceTask,
              addPerformanceTask,
              removePerformanceTask,
              generatePerformanceTasksAI,
              {
                title: "Performans Görevleri",
                icon: (
                  <ClipboardCheck className="w-4 h-4 text-purple-700 dark:text-purple-300" />
                ),
                placeholder: "Role play, proje, senaryo analizi...",
                description:
                  "Gerçek hayat durumlarında anlama ve transferi gösteren kompleks görevler",
                isLoading: loadingStates.performanceTasks,
                colorClasses: {
                  iconColor:
                    "bg-gradient-to-br from-purple-100 to-violet-200 dark:from-purple-900/40 dark:to-violet-800/40 border border-purple-300 dark:border-purple-600",
                  cardBg:
                    "bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/15 dark:to-violet-900/15 border-2",
                  cardBorder:
                    "border-purple-400 dark:border-purple-500 hover:border-purple-500 dark:hover:border-purple-400",
                  focusRing:
                    "focus:border-purple-600 dark:focus:border-purple-300 focus:ring-4 focus:ring-purple-500/30 dark:focus:ring-purple-400/30",
                  aiButton:
                    "bg-gradient-to-r from-purple-600 via-purple-700 to-violet-700 hover:from-purple-700 hover:via-purple-800 hover:to-violet-800 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40",
                  addButtonHover:
                    "hover:bg-gradient-to-br hover:from-purple-100 hover:to-violet-100 dark:hover:from-purple-900/30 dark:hover:to-violet-900/30 hover:border-purple-500",
                },
              },
            )}

            {/* Other Evidence - Teal Character */}
            {renderSectionCards(
              otherEvidence,
              updateOtherEvidence,
              addOtherEvidence,
              removeOtherEvidence,
              generateOtherEvidenceAI,
              {
                title: "Diğer Kanıtlar",
                icon: (
                  <Eye className="w-4 h-4 text-teal-700 dark:text-teal-300" />
                ),
                placeholder: "Test, form, gözlem, yazı...",
                description:
                  "Testler, kısa cevaplar, gözlemler ve akademik değerlendirmeler",
                isLoading: loadingStates.otherEvidence,
                colorClasses: {
                  iconColor:
                    "bg-gradient-to-br from-teal-100 to-cyan-200 dark:from-teal-900/40 dark:to-cyan-800/40 border border-teal-300 dark:border-teal-600",
                  cardBg:
                    "bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/15 dark:to-cyan-900/15 border-2",
                  cardBorder:
                    "border-teal-400 dark:border-teal-500 hover:border-teal-500 dark:hover:border-teal-400",
                  focusRing:
                    "focus:border-teal-600 dark:focus:border-teal-300 focus:ring-4 focus:ring-teal-500/30 dark:focus:ring-teal-400/30",
                  aiButton:
                    "bg-gradient-to-r from-teal-600 via-teal-700 to-cyan-700 hover:from-teal-700 hover:via-teal-800 hover:to-cyan-800 shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40",
                  addButtonHover:
                    "hover:bg-gradient-to-br hover:from-teal-100 hover:to-cyan-100 dark:hover:from-teal-900/30 dark:hover:to-cyan-900/30 hover:border-teal-500",
                },
              },
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🔒</div>
          <Text
            variant="h3"
            className="text-xl font-bold text-gray-600 dark:text-gray-400 mb-2"
          >
            Plan Oluşturma Gerekli
          </Text>
          <Text variant="p" className="text-gray-500 dark:text-gray-400">
            Değerlendirme yöntemlerinizi oluşturmak için önce "Programlar Arası
            Bileşenler" adımından plan oluşturmanız gerekir.
          </Text>
        </div>
      )}

      {/* Updated Tips Section */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4 hover:shadow-md transition-all duration-300">
        <div className="flex items-center space-x-2 mb-3">
          <Lightbulb className="w-4 h-4 text-yellow-500 animate-pulse" />
          <Text
            variant="h4"
            className="text-sm font-semibold text-gray-800 dark:text-gray-200"
          >
            UBD Değerlendirme İpuçları
          </Text>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          {/* Performance Tasks */}
          <div className="space-y-1 bg-orange-50/70 dark:bg-orange-900/10 rounded-lg p-3 border border-orange-200 dark:border-orange-700">
            <Text
              variant="h5"
              className="text-xs font-bold text-orange-800 dark:text-orange-200 mb-1"
            >
              Performans Görevleri
            </Text>
            <ul className="space-y-1 text-orange-700 dark:text-orange-300">
              <li>• Gerçek yaşam bağlamında uygulanabilir olmalı</li>
              <li>
                • Öğrencilerin öğrendiklerini göstermelerine olanak sağlamalı
              </li>
              <li>• Çoklu değerlendirme kriteri içermeli</li>
            </ul>
          </div>

          {/* Other Evidence */}
          <div className="space-y-1 bg-emerald-50/70 dark:bg-emerald-900/10 rounded-lg p-3 border border-emerald-200 dark:border-emerald-700">
            <Text
              variant="h5"
              className="text-xs font-bold text-emerald-800 dark:text-emerald-200 mb-1"
            >
              Diğer Kanıtlar
            </Text>
            <ul className="space-y-1 text-emerald-700 dark:text-emerald-300">
              <li>• Gözlem, öz değerlendirme, akran değerlendirme</li>
              <li>• Quiz, test, kontrol listesi</li>
              <li>• Günlük, portfolyo, yansıtma yazıları</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UbdAssessmentExperiencesStep;
